import lazyGettext from 'js-utils/lazy-gettext';
import PropTypes from 'prop-types';

export const EVENT_IMAGE_PLACEHOLDER_URL =
    'https://cdn.evbstatic.com/s3-build/perm_001/5287e3/django/images/pages/organizations/no-event-image-placeholder-2x.png';

export const URL_PARAM_NAME_SEARCH_QUERY = 'q';
export const URL_PARAM_NAME_PAGE_NUMBER = 'pg';
export const URL_PARAM_NAME_PAGE_SIZE = 'ps';
export const URL_PARAM_NAME_ORGANIZER_ID = 'org';

export const DEFAULT_SEARCH_QUERY = '';
export const DEFAULT_PAGE_NUMBER = 1;
export const DEFAULT_PAGE_SIZE = 20;
export const DEFAULT_ORGANIZER_ID = '-1';
export const DEFAULT_EVENT_PAGE_SIZE = 255;

export const ORDER_BY_START_ASC = 'start_asc';
export const ORDER_BY_START_DESC = 'start_desc';

export const API_STATUS_CANCELED = 'canceled';
export const API_STATUS_LIVE = 'live';
export const API_STATUS_STARTED = 'started';
export const API_STATUS_ENDED = 'ended';
export const API_STATUS_COMPLETED = 'completed';
export const API_STATUS_DRAFT = 'draft';

export const STATUS_FILTER_ALL = 'all';
export const STATUS_FILTER_PUBLISHED = 'published';
export const STATUS_FILTER_DRAFT = 'draft';
export const STATUS_FILTER_PAST = 'past';
export const STATUS_FILTER_FUTURE = 'future';
export const STATUS_FILTER_DRAFT_AND_PUBLISHED = 'draft_and_published';
export const STATUS_COUNTS_PUBLISHED = 'currentFuturePublished';
export const STATUS_COUNTS_DRAFT = 'draft';
export const STATUS_COUNTS_PAST = 'pastPublished';

export const SCHEDULE_PUBLISH_STATUS_SCHEDULED = 'scheduled';

export const SELECT_FIELD_FILTER_OPTIONS = [
    {
        value: STATUS_FILTER_ALL,
        display: lazyGettext('All'),
    },
    {
        value: STATUS_FILTER_PUBLISHED,
        display: lazyGettext('Published'),
    },
    {
        value: STATUS_FILTER_DRAFT,
        display: lazyGettext('Draft'),
    },
    {
        value: STATUS_FILTER_PAST,
        display: lazyGettext('Past'),
    },
];

export const DEFAULT_STATUS_FILTER = STATUS_FILTER_PUBLISHED;

export const STATUS_FILTERS = [
    STATUS_FILTER_ALL,
    STATUS_FILTER_PUBLISHED,
    STATUS_FILTER_DRAFT,
    STATUS_FILTER_PAST,
    STATUS_FILTER_FUTURE,
];
export const STATUS_FILTER_PROP_TYPE = PropTypes.oneOf(STATUS_FILTERS);

const EVENT_STATUS_INFO_PROPS = {
    status: PropTypes.string.isRequired,
    listed: PropTypes.bool,
    inviteOnly: PropTypes.bool,
    password: PropTypes.string,
    publishSettings: PropTypes.shape({
        schedulePublishDate: PropTypes.string,
        publishedDate: PropTypes.string,
    }).isRequired,
};

export const EVENT_STATUS_INFO_SHAPE = PropTypes.shape(EVENT_STATUS_INFO_PROPS);

export const EVENT_SALES_STATUS_INFO_SHAPE = PropTypes.shape({
    status: PropTypes.string.isRequired,
    publishSettings: PropTypes.shape({
        schedulePublishDate: PropTypes.string,
        publishedDate: PropTypes.string,
    }),
    eventSalesStatus: PropTypes.shape({
        salesStatus: PropTypes.string,
        message: PropTypes.string,
        messageType: PropTypes.string,
    }),
});

export const DATETIME_WITH_TIMEZONE_SHAPE = PropTypes.shape({
    timezone: PropTypes.string.isRequired,
    local: PropTypes.string.isRequired,
    utc: PropTypes.string.isRequired,
});

export const MULTIPART_TEXT_SHAPE = PropTypes.shape({
    text: PropTypes.string,
    html: PropTypes.string,
});

export const EVENT_SHAPE = PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: MULTIPART_TEXT_SHAPE.isRequired,
    description: MULTIPART_TEXT_SHAPE.isRequired,
    url: PropTypes.string.isRequired,
    start: DATETIME_WITH_TIMEZONE_SHAPE.isRequired,
    end: DATETIME_WITH_TIMEZONE_SHAPE.isRequired,
    organizationId: PropTypes.string.isRequired,
    created: PropTypes.string.isRequired,
    changed: PropTypes.string.isRequired,
    capacity: PropTypes.number.isRequired,
    capacityIsCustom: PropTypes.bool,
    currency: PropTypes.string.isRequired,
    txTimeLimit: PropTypes.number.isRequired,
    locale: PropTypes.string.isRequired,
    privacySetting: PropTypes.string.isRequired,
    source: PropTypes.string,
    version: PropTypes.string.isRequired,
    organizerId: PropTypes.string.isRequired,
    ...EVENT_STATUS_INFO_PROPS,
    shareable: PropTypes.bool,
    onlineEvent: PropTypes.bool,
    showRemaining: PropTypes.bool,
    hideStartDate: PropTypes.bool,
    hideEndDate: PropTypes.bool,
    isLocked: PropTypes.bool,
    isSeries: PropTypes.bool,
    isSeriesParent: PropTypes.bool,
    isReservedSeating: PropTypes.bool,
    isFree: PropTypes.bool,
    categoryId: PropTypes.string,
    subcategoryId: PropTypes.string,
    formatId: PropTypes.string,
    salesDataWithNull: PropTypes.shape({
        totalTicketSales: PropTypes.number.isRequired,
        maxPossibleTicketSales: PropTypes.number.isRequired,
        net: PropTypes.string,
        gross: PropTypes.string,
    }),
    logoId: PropTypes.string,
    logo: PropTypes.shape({
        cropMask: PropTypes.shape({
            topLeft: PropTypes.shape({
                x: PropTypes.number.isRequired,
                y: PropTypes.number.isRequired,
            }).isRequired,
            width: PropTypes.number.isRequired,
            height: PropTypes.number.isRequired,
        }).isRequired,
        original: PropTypes.shape({
            url: PropTypes.string.isRequired,
            width: PropTypes.number.isRequired,
            height: PropTypes.number.isRequired,
        }),
        id: PropTypes.string.isRequired,
        url: PropTypes.string.isRequired,
        aspectRatio: PropTypes.string.isRequired,
        edgeColor: PropTypes.string.isRequired,
        edgeColorSet: PropTypes.bool,
    }),
    venueId: PropTypes.string,
    venue: PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        latitude: PropTypes.string,
        longitude: PropTypes.string,
        address: PropTypes.shape({
            address1: PropTypes.string.isRequired,
            address2: PropTypes.string,
            city: PropTypes.string.isRequired,
            region: PropTypes.string,
            postalCode: PropTypes.string,
            country: PropTypes.string.isRequired,
            latitude: PropTypes.string,
            longitude: PropTypes.string,
            localizedAddressDisplay: PropTypes.string.isRequired,
            localizedAreaDisplay: PropTypes.string.isRequired,
            localizedMultiLineAddressDisplay: PropTypes.arrayOf(
                PropTypes.string,
            ).isRequired,
        }).isRequired,
        ageRestriction: PropTypes.string,
        capacity: PropTypes.number,
    }),
});

// Event API expansions
export const DEFAULT_EVENT_EXPANSIONS =
    'venue,logo,sales_data_with_null,publish_settings,event_sales_status';
export const EVENT_VENUE_EXPANSION = 'venue';

// Event Sales Status
export const EVENT_SALES_STATUS_ON_SALE = 'on_sale';
export const EVENT_SALES_STATUS_NOT_YET_ON_SALE = 'not_yet_on_sale';
export const EVENT_SALES_STATUS_NOT_YET_ON_SALE_ACCESS_CODED =
    'not_yet_on_sale_access_coded';
export const EVENT_SALES_STATUS_SALES_ENDED = 'sales_ended';
export const EVENT_SALES_STATUS_SOLD_OUT = 'sold_out';
export const EVENT_SALES_STATUS_UNAVAILABLE = 'unavailable';

export const EVENT_SALES_STATUS_ICON_GREY_COLOR = 'grey-500';
export const EVENT_SALES_STATUS_ICON_GREEN_COLOR = 'ui-green';
export const EVENT_SALES_STATUS_ICON_RED_COLOR = 'ui-red';
export const EVENT_SALES_STATUS_ICON_YELLOW_COLOR = 'tropical-yellow-700';

export const EVENT_SALES_STATUS_MESSAGE_TYPE_DEFAULT = 'default';
export const EVENT_SALES_STATUS_MESSAGE_TYPE_CUSTOM = 'custom';

export const EVENT_SALES_STATUS_MESSAGE_CODE_SALES_ENDED =
    'tickets_with_sales_ended';
export const EVENT_SALES_STATUS_MESSAGE_CODE_AT_THE_DOOR =
    'tickets_at_the_door';

export const EVENT_SALES_STATUS_ICON_COLOR_MAP = {
    [EVENT_SALES_STATUS_ON_SALE]: EVENT_SALES_STATUS_ICON_GREEN_COLOR,
    [EVENT_SALES_STATUS_NOT_YET_ON_SALE]: EVENT_SALES_STATUS_ICON_YELLOW_COLOR,
    [EVENT_SALES_STATUS_NOT_YET_ON_SALE_ACCESS_CODED]: EVENT_SALES_STATUS_ICON_YELLOW_COLOR,
    [EVENT_SALES_STATUS_SALES_ENDED]: EVENT_SALES_STATUS_ICON_GREY_COLOR,
    [EVENT_SALES_STATUS_SOLD_OUT]: EVENT_SALES_STATUS_ICON_GREY_COLOR,
    [EVENT_SALES_STATUS_UNAVAILABLE]: EVENT_SALES_STATUS_ICON_GREY_COLOR,
};

export const ORGANIZER_SHAPE = PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
    description: MULTIPART_TEXT_SHAPE.isRequired,
    longDescription: MULTIPART_TEXT_SHAPE.isRequired,
    url: PropTypes.string.isRequired,
    numPastEvents: PropTypes.number.isRequired,
    numFutureEvents: PropTypes.number.isRequired,
    organizationId: PropTypes.string.isRequired,
    logoId: PropTypes.string,
});

// Values for EventsList actions in EditListItem
export const VIEW_ACTION_TYPE = 'view';
export const EDIT_ACTION_TYPE = 'edit';
export const COPY_URL_ACTION_TYPE = 'copyUrl';
export const COPY_EVENT_ACTION_TYPE = 'copyEvent';
export const POSTPONE_ACTION_TYPE = 'postpone';

export const GA_MANAGE_EVENTS_BUTTON_DIMENSION = 'myevents';

export const DONATION_PRICE_CHANGE_NOTIFIED_COUNTRIES = [
    'US',
    'CA',
    'UK',
    'IE',
    'AU',
    'DE',
    'NL',
    'ES',
];

export const DONATION_PRICE_CHANGE_NOTIFICATION_TEXT = lazyGettext(
    "To support our event creators, we've lowered fees for all donation ticket types.",
);

export const DONATION_PRICE_CHANGE_NOTIFICATION_CALL_ACTION = lazyGettext(
    'Learn more',
);
export const DONATION_PRICE_CHANGE_NOTIFICATION_CALL_ACTION_LINK =
    '/support/articleredirect?anum=6474';
